"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireDefault(require("react"));

var _classnames = _interopRequireDefault(require("classnames"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _button = _interopRequireDefault(require("../button"));

var _popoverTrigger = _interopRequireDefault(require("../popover-trigger"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

let tooltipCounter = 0;
/**
 * Wrap a trigger element so that when it is hovered or focused a tooltip
 * appears.
 *
 * Tooltip extends [PopoverTrigger](#popovertrigger) and configures the
 * popover and trigger for accessibility.
 */

class Tooltip extends _react.default.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      visible: false,
      tooltipId: null
    });
  }

  componentDidMount() {
    // Save the application for the random generated id until after mount
    // so that it doesn't mess up the relation of static/dynamic React code.
    // Generating unique ids for server-rendered code without breaking
    // the checksum is very hard!
    tooltipCounter += 1;
    this.setState({
      tooltipId: `tooltip-${tooltipCounter}`
    });
  }

  getContent() {
    const {
      content
    } = this.props;

    if (typeof content === 'function') {
      return content();
    } else {
      return content;
    }
  }

  renderTrigger() {
    const {
      children,
      testId
    } = this.props;
    const {
      tooltipId
    } = this.state;

    if (!children) {
      return null;
    }

    const triggerProps = {
      'aria-describedby': tooltipId
    };

    if (testId) {
      triggerProps['data-test'] = `${testId}-trigger`;
    }

    if (typeof children === 'function') {
      return children(triggerProps);
    }

    if (children.type && typeof children.type === 'string') {
      return /*#__PURE__*/_react.default.cloneElement(children, triggerProps);
    }

    if (children.type && children.type === _button.default) {
      return /*#__PURE__*/_react.default.cloneElement(children, {
        passthroughProps: triggerProps
      });
    }

    throw new Error('Tooltip requires a child that is a function, a regular DOM node, or a Button. If your trigger is a different custom component, pass a function as the child.');
  }

  render() {
    const {
      props,
      state
    } = this;
    const popoverPassthroughProps = {
      id: state.tooltipId,
      // Override the "dialog" role set by popover
      role: 'tooltip'
    };

    if (props.testId) {
      popoverPassthroughProps['data-test'] = `${props.testId}-tooltip`;
    }

    const bodyClasses = (0, _classnames.default)({
      'txt-s': props.textSize === 's',
      'txt-xs': props.textSize === 'xs',
      wmax120: props.maxWidth === 'small',
      wmax240: props.maxWidth === 'medium'
    });

    const content = /*#__PURE__*/_react.default.createElement("div", {
      className: bodyClasses
    }, this.getContent());

    return /*#__PURE__*/_react.default.createElement(_popoverTrigger.default, {
      content: content,
      renderHiddenContent: true,
      disabled: props.disabled,
      block: props.block,
      respondsToClick: props.respondsToClick,
      respondsToHover: true,
      respondsToFocus: true,
      receiveFocus: false,
      popoverProps: {
        hasPointer: true,
        placement: props.placement,
        alignment: props.alignment,
        coloring: props.coloring,
        padding: props.padding,
        hideWhenAnchorIsOffscreen: true,
        passthroughProps: popoverPassthroughProps
      }
    }, this.renderTrigger());
  }

}

exports.default = Tooltip;
Tooltip.propTypes = {
  /**
   * The trigger element.
   *
   * If your element is a DOM element
   * (e.g. `<button>`, `<a>`, `<span>`, `<div>`),
   * you can pass it directly and it will have accessibility-related props
   * automatically added to it.
   *
   * If your element is a custom React component, you need to provide a
   * function as the child. The function will be invoked with a `triggerProps`
   * object, and you need to apply those `triggerProps` in such a way that
   * they end up on the DOM element of the trigger.
   */
  children: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]).isRequired,

  /**
   * The tooltip content. This can either be a string, valid JSX, or a function
   * returning either.
   */
  content: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]).isRequired,

  /**
   * Placement of the tooltip in relation to the trigger element.
   */
  placement: _propTypes.default.oneOf(['top', 'bottom', 'left', 'right']),

  /**
   * Alignment of the tooltip's edge in relation to the trigger element.
   */
  alignment: _propTypes.default.oneOf(['top', 'bottom', 'left', 'right', 'center']),

  /**
   * If `true`, the tooltip will not appear.
   */
  disabled: _propTypes.default.bool,

  /**
   * `'light'`, `'dark'`, `'warning'`, or `'error'`.
   */
  coloring: _propTypes.default.oneOf(['light', 'dark', 'warning', 'error']),

  /**
   * `'xs'` (extra small), `'s'` (small), or `'none'` (no size class).
   */
  textSize: _propTypes.default.oneOf(['xs', 's', 'none']),

  /**
   * `'small'`, `'medium'`, or `'none'` (no `wmax*` class).
   */
  maxWidth: _propTypes.default.oneOf(['small', 'medium', 'none']),

  /**
   * `'small'` or `'none'`.
   */
  padding: _propTypes.default.oneOf(['small', 'none']),

  /**
   * If `true`, tooltip can be opened with a mouse click.
   */
  respondsToClick: _propTypes.default.bool,

  /**
   * If `true`, the element will be `block` displayed instead of `inline-block`.
   *
   * This is sometimes necessary to get your pixel-perfect layout, if you don't
   * want the extra line-height that wraps inline elements. Typically, you
   * should only set `block` to `true` if the parent element is controlling
   * width (in a layout that uses flexbox, absolute positioning, or floats).
   */
  block: _propTypes.default.bool,

  /**
   * Added as `data-test` to the tooltip element.
   */
  testId: _propTypes.default.string
};
Tooltip.defaultProps = {
  placement: 'top',
  alignment: 'center',
  coloring: 'light',
  disabled: false,
  respondsToClick: false,
  padding: 'small',
  block: false,
  textSize: 's',
  maxWidth: 'medium'
};